import React, { useEffect, useMemo, useRef, useState } from 'react';
import Footer from '../components/Footer';
import './PlaceholderPage.css';

const formatCpfCnpj = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);

  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const parseCurrencyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const text = String(value).trim();
  const cleaned = text.replace(/[^\d,.-]/g, '');

  if (!cleaned) {
    return null;
  }

  if (cleaned.includes(',') && cleaned.includes('.')) {
    const normalized = cleaned.replace('.', '').replace(',', '.');
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  if (cleaned.includes(',')) {
    const normalized = cleaned.replace(',', '.');
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  if (cleaned.includes('.')) {
    const numericValue = Number(cleaned);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  const numericValue = Number(cleaned);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  // Quando a API retorna um valor inteiro em centavos, como 5990, converte para 59.90.
  if (numericValue >= 1000) {
    return numericValue / 100;
  }

  return numericValue;
};

const formatMoney = (value) => {
  const numericValue = parseCurrencyValue(value);

  if (numericValue === null) {
    return '—';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericValue);
};

const getFirstName = (value) => {
  const text = String(value || '').trim();

  if (!text) {
    return 'Cliente';
  }

  return text.split(/\s+/)[0] || 'Cliente';
};

const normalizeContractId = (value) => String(value ?? '').replace(/[^\d]/g, '');

const getInvoiceContractId = (invoice = {}) => (
  String(
    invoice.id_contrato_avulso ||
    invoice.id_contrato ||
    invoice.contrato_id ||
    invoice.contrato ||
    ''
  )
);

const getInvoiceLink = (invoice = {}, cpfCnpjNormalized = '') => {
  const faturaId = invoice.fatura_id || invoice.id;

  if (faturaId && cpfCnpjNormalized) {
    const endpoint = window.MSA_SEGUNDA_VIA_URL || 'http://localhost/api-faynet/testar.php';
    return `${endpoint}?action=boleto&cpf_cnpj=${encodeURIComponent(cpfCnpjNormalized)}&fatura_id=${encodeURIComponent(faturaId)}`;
  }

  return (
    invoice.gateway_link ||
    invoice.boleto_link ||
    invoice.link ||
    invoice.url ||
    invoice.pdf_url ||
    invoice.link_boleto ||
    ''
  );
};

const getPixQrCodeSrc = (value) => {
  if (!value) {
    return '';
  }

  if (String(value).trim().startsWith('<svg')) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
  }

  return value;
};

const getInvoicePixCode = (invoice = {}) => (
  invoice.pix_brcode || invoice.pix_copia_e_cola || ''
);

const getInvoicePixQrCode = (invoice = {}) => (
  invoice.pix_qrcode || invoice.pix_qrcode_svg || ''
);

function SegundaViaPage() {
  const contratosSectionRef = useRef(null);
  const cpfCnpjInputRef = useRef(null);
  const contractPanelRefs = useRef({});
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [cliente, setCliente] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [expandedInvoiceId, setExpandedInvoiceId] = useState('');
  const [expandedPixInvoiceId, setExpandedPixInvoiceId] = useState('');
  const [copiedPixInvoiceId, setCopiedPixInvoiceId] = useState('');
  const [pixLoadingInvoiceId, setPixLoadingInvoiceId] = useState('');
  const [pixToastVisible, setPixToastVisible] = useState(false);
  const [boletoLoadingInvoiceId, setBoletoLoadingInvoiceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');

    document.title = '2ª Via da Fatura | Faynet';
    description?.setAttribute(
      'content',
      'Consulte sua 2ª Via da Fatura Faynet informando CPF ou CNPJ. Acesse a fatura, veja o vencimento e escolha Pix, cartão de crédito ou boleto.'
    );

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) {
        description.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const visibleFaturas = useMemo(() => {
    if (!selectedContractId) {
      return faturas;
    }

    const selectedNormalized = normalizeContractId(selectedContractId);

    return faturas.filter((invoice) => {
      const invoiceContractId = getInvoiceContractId(invoice);
      return selectedNormalized !== '' && normalizeContractId(invoiceContractId) === selectedNormalized;
    });
  }, [faturas, selectedContractId]);

  const normalizedCpfCnpj = useMemo(() => (cpfCnpj || '').replace(/\D/g, ''), [cpfCnpj]);

  const handleOpenBoleto = async (invoice, invoiceId) => {
    const invoiceLink = getInvoiceLink(invoice, normalizedCpfCnpj);

    if (!invoiceLink) {
      return;
    }

    setBoletoLoadingInvoiceId(invoiceId);

    try {
      const response = await fetch(invoiceLink);
      const contentType = response.headers.get('Content-Type') || '';

      if (response.ok && contentType.includes('application/pdf')) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        return;
      }

      const data = await response.json().catch(() => null);

      setError(data?.error || 'Não foi possível abrir a 2ª via agora. Tente novamente em instantes.');
    } catch {
      setError('Não foi possível abrir a 2ª via agora. Tente novamente em instantes.');
    } finally {
      setBoletoLoadingInvoiceId('');
    }
  };

  const refreshInvoiceData = async (normalizedDoc) => {
    const endpoint = window.MSA_SEGUNDA_VIA_URL || 'http://localhost/api-faynet/testar.php';
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 25000);

    let response;
    try {
      response = await fetch(`${endpoint}?cpf_cnpj=${encodeURIComponent(normalizedDoc)}&format=json`, {
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeoutId);
    }

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    // Mesmo com status não-2xx, o backend pode ter retornado o JSON com o motivo real (ex.: cliente não encontrado).
    if (!data) {
      throw new Error('Erro ao atualizar a fatura.');
    }

    const nextContratos = Array.isArray(data.contratos) ? data.contratos : [];
    const nextFaturas = Array.isArray(data.faturas) ? data.faturas : [];

    setCliente(data.cliente || null);
    setContratos(nextContratos);
    setFaturas(nextFaturas);

    return data;
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    const normalizedDoc = (cpfCnpj || '').replace(/\D/g, '');

    if (normalizedDoc.length < 11 || normalizedDoc.length > 14) {
      setCliente(null);
      setContratos([]);
      setFaturas([]);
      setSelectedContractId('');
      setError(normalizedDoc.length === 0
        ? 'Digite seu CPF ou CNPJ para consultar a 2ª Via da Fatura.'
        : 'Confira o CPF ou CNPJ informado. Digite os 11 ou 14 números para consultar a 2ª Via da Fatura.');
      cpfCnpjInputRef.current?.focus();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await refreshInvoiceData(normalizedDoc);
      const nextContratos = Array.isArray(data.contratos) ? data.contratos : [];
      const nextFaturas = Array.isArray(data.faturas) ? data.faturas : [];
      const nextSelectedContract = data.contrato_selecionado || nextContratos[0]?.id || '';

      setCliente(data.cliente || null);
      setContratos(nextContratos);
      setFaturas(nextFaturas);
      setSelectedContractId(nextSelectedContract);

      if (data.cliente) {
        window.setTimeout(() => {
          contratosSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
      }

      if (!data.success || (!data.cliente && nextContratos.length === 0)) {
        setError(data.error || 'Nenhum cliente encontrado para esse CPF/CNPJ.');
      }
    } catch (err) {
      setError(err?.name === 'AbortError'
        ? 'A consulta está demorando mais que o esperado. Tente novamente em instantes.'
        : 'Não conseguimos acessar sua fatura agora. Verifique sua conexão e tente novamente em alguns instantes.');
      setCliente(null);
      setContratos([]);
      setFaturas([]);
      setSelectedContractId('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="segunda-via-page">
      <section className="segunda-via-page__hero">
        <div className="segunda-via-page__shell">
          <img
            src="/index/banner-2via.png"
            alt="Faynet - 2ª Via da Fatura"
            className="segunda-via-page__banner"
          />

          <div className="segunda-via-page__header">
            <span className="segunda-via-page__eyebrow">Faynet</span>
            <h1>2ª Via da Fatura</h1>
            <p>Consulte sua fatura em aberto e escolha a forma de pagamento mais conveniente para você.</p>
          </div>

          <form className="segunda-via-page__search" onSubmit={handleSearch}>
            <label htmlFor="cpf-cnpj" className="segunda-via-page__label">Digite seu CPF ou CNPJ para consultar a fatura</label>
            <div className="segunda-via-page__search-row">
              <input
                id="cpf-cnpj"
                name="cpfCnpj"
                type="text"
                ref={cpfCnpjInputRef}
                value={cpfCnpj}
                onChange={(event) => {
                  setCpfCnpj(formatCpfCnpj(event.target.value));
                  if (error) {
                    setError('');
                  }
                }}
                placeholder="Digite seu CPF ou CNPJ"
                autoComplete="off"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'segunda-via-message' : undefined}
              />
              <button type="submit" disabled={loading} className={loading ? 'is-loading' : ''}>
                {loading ? (
                  <span className="button-loader" aria-label="Consultando" />
                ) : (
                  <>
                    <span className="search-button-icon" aria-hidden="true" />
                    <span>Consultar fatura</span>
                  </>
                )}
              </button>
            </div>
            {error && (
              <div
                id="segunda-via-message"
                className="segunda-via-page__message segunda-via-page__message--error"
                role="alert"
              >
                <strong>A consulta não foi concluída</strong>
                <span>{error}</span>
              </div>
            )}
          </form>

          <section className="segunda-via-page__payment-guide" aria-labelledby="payment-guide-title">
            <div className="segunda-via-page__payment-guide-heading">
              <span className="segunda-via-page__payment-guide-icon" aria-hidden="true">R$</span>
              <div>
                <span className="segunda-via-page__eyebrow">Facilidades para pagar</span>
                <h2 id="payment-guide-title">Escolha a melhor forma de pagamento</h2>
              </div>
            </div>
            <div className="segunda-via-page__payment-options">
              <div className="segunda-via-page__payment-option">
                <strong>Pix ou cartão de crédito</strong>
                <p>São pagamentos instantâneos e a confirmação acontece rapidamente.</p>
              </div>
              <div className="segunda-via-page__payment-option">
                <strong>Boleto</strong>
                <p>A compensação depende do prazo bancário e pode levar alguns dias úteis.</p>
              </div>
              <div className="segunda-via-page__payment-option">
                <strong>Pagamento recorrente</strong>
                <p>O valor é descontado mensalmente no cartão, sem comprometer o limite total. É necessário ter limite disponível na data do vencimento.</p>
              </div>
            </div>
          </section>

          {cliente && (
            <div className="segunda-via-page__card segunda-via-page__card--primary" ref={contratosSectionRef}>
              <div className="segunda-via-page__welcome">
                <span>Olá, {getFirstName(cliente.razao || cliente.nome || 'Cliente')}</span>
                <small>{contratos.length > 0 ? 'Seu contrato está disponível abaixo.' : 'Confira a situação da sua fatura abaixo.'}</small>
              </div>
            </div>
          )}

          {cliente && contratos.length === 0 && (
            <div className="segunda-via-page__card">
              <div className="segunda-via-page__invoice-empty">
                <span className="segunda-via-page__invoice-empty-icon" role="img" aria-label="Tudo certo">😊</span>
                <strong>Nenhuma fatura em aberto</strong>
                <p>Você está em dia com a Faynet. Assim que houver uma nova fatura, ela aparecerá por aqui.</p>
              </div>
            </div>
          )}

          {contratos.length > 0 && (
            <div className="segunda-via-page__card">
              <h2 className="segunda-via-page__card-title">
                <span className="segunda-via-page__card-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h5.379a2.5 2.5 0 0 1 1.767.732l1.622 1.621A2.5 2.5 0 0 0 17.035 7H17.5A2.5 2.5 0 0 1 20 9.5v8A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M8.5 13.5h7M8.5 16.5h4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
                Contratos ativos
              </h2>
              <div className="segunda-via-page__contract-list">
                {contratos.map((contract) => {
                  const contractId = String(contract.id || '');
                  const isSelected = normalizeContractId(selectedContractId) === normalizeContractId(contractId);

                  const isOpen = isSelected;

                  return (
                    <div key={contractId} className={`segunda-via-page__contract-wrapper ${isSelected ? 'is-selected' : ''}`}>
                      <button
                        type="button"
                        className={`segunda-via-page__contract ${isSelected ? 'is-selected' : ''}`}
                        aria-label={isSelected ? 'Fechar contrato' : 'Abrir contrato'}
                        onClick={() => {
                          const willOpen = normalizeContractId(selectedContractId) !== normalizeContractId(contractId);
                          setSelectedContractId((prev) => (normalizeContractId(prev) === normalizeContractId(contractId) ? '' : contractId));
                          setExpandedInvoiceId('');
                          setExpandedPixInvoiceId('');

                          if (willOpen) {
                            window.setTimeout(() => {
                              contractPanelRefs.current[contractId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 200);
                          }
                        }}
                      >
                        <div className="segunda-via-page__contract-head">
                          <div>
                            <span className="segunda-via-page__contract-number">Contrato {contractId}</span>
                            <small>{contract.contrato || 'Contrato ativo'}</small>
                          </div>
                          <span className={`segunda-via-page__contract-toggle ${isSelected ? 'is-open' : ''}`} aria-hidden="true">
                            <span className="segunda-via-page__contract-toggle-icon" />
                          </span>
                        </div>
                      </button>

                      {isOpen && (
                        <div
                          className="segunda-via-page__contract-panel is-open"
                          ref={(el) => { contractPanelRefs.current[contractId] = el; }}
                        >
                          {visibleFaturas.length === 0 ? (
                            <div className="segunda-via-page__invoice-empty">
                              <span className="segunda-via-page__invoice-empty-icon" role="img" aria-label="Tudo certo">😊</span>
                              <strong>Nenhuma fatura em aberto</strong>
                              <p>Você está em dia com a Faynet. Assim que houver uma nova fatura, ela aparecerá por aqui.</p>
                            </div>
                          ) : (
                          <div className="segunda-via-page__invoice-list segunda-via-page__invoice-list--nested">
                            {visibleFaturas.map((invoice, index) => {
                              const invoiceLink = getInvoiceLink(invoice, normalizedCpfCnpj);
                              const invoiceId = invoice.id || `fatura-${index + 1}`;
                              const isExpanded = expandedInvoiceId === invoiceId;
                              const isPixExpanded = expandedPixInvoiceId === invoiceId;
                              const pixCode = getInvoicePixCode(invoice);
                              const pixQrCodeSrc = getPixQrCodeSrc(getInvoicePixQrCode(invoice));
                              const hasPix = Boolean(pixCode || pixQrCodeSrc);

                              return (
                                <div key={`${invoiceId}-${contractId}`} className="segunda-via-page__invoice-item">
                                  <div className="segunda-via-page__invoice-main">
                                    <div className="segunda-via-page__invoice-header">
                                      <span className="segunda-via-page__invoice-icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M6 2.5h9l3 3V21a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 21V3a.5.5 0 0 1 .5-.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                                          <path d="M15 2.5V5a1 1 0 0 0 1 1h2.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                                          <path d="M9 12h6M9 15.5h6M9 8.5h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                        </svg>
                                      </span>
                                      <p className="segunda-via-page__invoice-title">Fatura {invoiceId}</p>
                                      <span className="segunda-via-page__status-badge">Aberto</span>
                                    </div>
                                    <p><strong>Contrato:</strong> {contractId}</p>
                                    <p><strong>Vencimento:</strong> {invoice.data_vencimento || '—'}</p>
                                    <p><strong>Valor:</strong> {formatMoney(invoice.valor_aberto || invoice.valor || invoice.valor_total || 0)}</p>
                                  </div>

                                  <div className="segunda-via-page__invoice-actions">
                                    <button
                                      type="button"
                                      className={`segunda-via-page__toggle ${isExpanded ? 'is-open' : ''}`}
                                      onClick={() => setExpandedInvoiceId(isExpanded ? '' : invoiceId)}
                                    >
                                      <span className="segunda-via-page__toggle-icon" aria-hidden="true" />
                                      <span>{isExpanded ? 'Fechar detalhes' : 'Ver detalhes'}</span>
                                    </button>

                                    {hasPix && (
                                      <button
                                        type="button"
                                        className={`segunda-via-page__pix-toggle ${isPixExpanded ? 'is-open' : ''}`}
                                        onClick={async () => {
                                          if (isPixExpanded) {
                                            setExpandedPixInvoiceId('');
                                            return;
                                          }

                                          setCopiedPixInvoiceId('');
                                          setExpandedPixInvoiceId(invoiceId);
                                          setPixLoadingInvoiceId(invoiceId);
                                          try {
                                            await refreshInvoiceData((cpfCnpj || '').replace(/\D/g, ''));
                                          } catch {
                                            setError('Não foi possível atualizar os dados do Pix. Tente novamente.');
                                          } finally {
                                            setPixLoadingInvoiceId('');
                                          }
                                        }}
                                        disabled={pixLoadingInvoiceId === invoiceId}
                                      >
                                        <span className="segunda-via-page__pix-icon" aria-hidden="true">
                                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="4" y="4" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
                                            <rect x="14" y="4" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
                                            <rect x="4" y="14" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
                                            <path d="M14 14h3v3h-3v3h6v-6h-3v-3h-3v3Z" fill="currentColor" />
                                          </svg>
                                        </span>
                                        <span>{pixLoadingInvoiceId === invoiceId ? 'Atualizando Pix...' : (isPixExpanded ? 'Fechar Pix' : 'Pagar com Pix')}</span>
                                      </button>
                                    )}

                                    {invoiceLink ? (
                                      <button
                                        type="button"
                                        className="segunda-via-page__link"
                                        onClick={() => handleOpenBoleto(invoice, invoiceId)}
                                        disabled={boletoLoadingInvoiceId === invoiceId}
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                          <path d="M14 5h5v5M19 5l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        </svg>
                                        <span>{boletoLoadingInvoiceId === invoiceId ? 'Abrindo 2ª via...' : 'Consultar 2ª via'}</span>
                                      </button>
                                    ) : (
                                      <span className="segunda-via-page__empty-link">Sem boleto disponível</span>
                                    )}
                                  </div>

                                  {isExpanded && (
                                    <div className="segunda-via-page__invoice-details">
                                      <p><strong>Código de barras:</strong> {invoice.linha_digitavel ? 'Disponível ao abrir a 2ª via do boleto' : 'Não disponível'}</p>
                                      <p><strong>Observação:</strong> Dados do boleto e da linha digitável ficam protegidos para evitar exposição indevida de informações sensíveis.</p>
                                    </div>
                                  )}

                                  {isPixExpanded && hasPix && (
                                    <div className="segunda-via-page__pix-details">
                                      <div>
                                        <strong>Pague com Pix</strong>
                                        <p>Escaneie o QR Code ou copie o código Pix para pagar sua fatura.</p>
                                      </div>
                                      {pixQrCodeSrc && (
                                        <img
                                          src={pixQrCodeSrc}
                                          alt="QR Code Pix da fatura"
                                          className="segunda-via-page__pix-qr-code"
                                        />
                                      )}
                                      {pixCode && (
                                        <div className="segunda-via-page__pix-copy">
                                          <label htmlFor={`pix-code-${invoiceId}`}>Código Pix copia e cola</label>
                                          <textarea
                                            id={`pix-code-${invoiceId}`}
                                            readOnly
                                            value={pixCode}
                                            rows={3}
                                          />
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              try {
                                                await navigator.clipboard.writeText(pixCode);
                                                setCopiedPixInvoiceId(invoiceId);
                                                setPixToastVisible(true);
                                                window.setTimeout(() => setPixToastVisible(false), 2600);
                                              } catch {
                                                setCopiedPixInvoiceId('');
                                              }
                                            }}
                                          >
                                            <span className="segunda-via-page__copy-icon" aria-hidden="true">⧉</span>
                                            <span>{copiedPixInvoiceId === invoiceId ? 'Código copiado' : 'Copiar código Pix'}</span>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {!loading && !error && !cliente && !contratos.length && (
            <div className="segunda-via-page__placeholder">
              Digite um CPF ou CNPJ para localizar os contratos ativos e as faturas em aberto.
            </div>
          )}
        </div>
      </section>
      {pixToastVisible && (
        <div className="segunda-via-page__pix-toast" role="status" aria-live="polite">
          <span className="segunda-via-page__pix-toast-icon" aria-hidden="true">✓</span>
          <span>Código Pix copiado!</span>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default SegundaViaPage;
